"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Form } from "@/components/ui/form";
import { toast } from "sonner";
import {
  TextField,
  SelectField,
  TextareaField,
  SwitchField,
  DateField,
} from "@admin/_components/form-fields";
import { RichHtmlEditor } from "@admin/_components/rich-html-editor";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useSession } from "next-auth/react";

// ── Timezone name map (C-12) ──────────────────────────────────────────────────

const TIMEZONE_NAMES: Record<string, string> = {
  "America/New_York": "Eastern Standard Time",
  "America/Chicago": "Central Standard Time",
  "America/Denver": "Mountain Standard Time",
  "America/Los_Angeles": "Pacific Standard Time",
  "America/Anchorage": "Alaska Standard Time",
  "America/Honolulu": "Hawaii-Aleutian Standard Time",
  "America/Phoenix": "Arizona Time (No DST)",
  "America/Puerto_Rico": "Puerto Rico Time",
  "America/Guam": "Guam Time",
  "America/Virgin": "Virgin Islands Time",
};

const TIMEZONE_OPTIONS = Object.entries(TIMEZONE_NAMES).map(([value, label]) => ({
  value,
  label,
}));

// ── Day options ───────────────────────────────────────────────────────────────

const DAYS = [
  { key: "sun", label: "Sun" },
  { key: "mon", label: "Mon" },
  { key: "tues", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thurs", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
] as const;

type DayKey = (typeof DAYS)[number]["key"];

const WEEKDAYS: DayKey[] = ["mon", "tues", "wed", "thurs", "fri"];
const WEEKEND: DayKey[] = ["sun", "sat"];
const EVERYDAY: DayKey[] = ["sun", "mon", "tues", "wed", "thurs", "fri", "sat"];

// ── Role options (C-06) ───────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "is_astrologer", label: "Astrologer" },
  { key: "is_tarotreader", label: "Tarot Reader" },
] as const;

// ── Quarter options (C-10) ────────────────────────────────────────────────────

const QUARTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "spring_quarter", label: "Spring Quarter" },
  { key: "summer_quarter", label: "Summer Quarter" },
  { key: "autumn_quarter", label: "Autumn Quarter" },
  { key: "winter_quarter", label: "Winter Quarter" },
] as const;

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    title: z.string().min(1, "Event name is required"),
    priority: z.string().min(1, "Priority is required"),
    category: z.string().min(1, "Category is required"),
    color_code: z.string().min(1, "Color code is required"),
    start_date: z.string().min(1, "Start date is required"),
    start_time: z.string().min(1, "Start time is required"),
    end_date: z.string().min(1, "End date is required"),
    end_time: z.string().min(1, "End time is required"),
    timezoneValue: z.string().min(1, "Timezone is required"),
    displayed_event_for: z.string().min(1, "Displayed event for is required"),
    description: z.string().optional().default(""),
    // Role multi-select
    role: z.array(z.string()).min(1, "At least one role is required"),
    // Recurring
    is_recurring: z.boolean().default(false),
    recurring_days: z.array(z.string()).optional().default([]),
    // Subscription
    subscription_availability: z.boolean().default(false),
    notification_template: z.string().optional().default(""),
    // Quarter multi-select
    quarter: z.array(z.string()).min(1, "At least one quarter is required"),
    // Switches (C-11)
    visible_in_frontend: z.boolean().default(false),
    not_available_slot: z.boolean().default(false),
    holiday_event: z.boolean().default(false),
    status: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.subscription_availability && !data.notification_template?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notification template is required when subscription availability is enabled",
        path: ["notification_template"],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

// ── Checkbox Group helper ─────────────────────────────────────────────────────

function CheckboxGroup({
  label,
  options,
  value,
  onChange,
  error,
}: {
  label: string;
  options: readonly { key: string; label: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
}) {
  function toggle(key: string) {
    if (value.includes(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      onChange([...value, key]);
    }
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex flex-wrap gap-3">
        {options.map((opt) => (
          <label
            key={opt.key}
            className="flex cursor-pointer items-center gap-2 text-sm select-none"
          >
            <Checkbox
              checked={value.includes(opt.key)}
              onCheckedChange={() => toggle(opt.key)}
            />
            {opt.label}
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Calendar Event Form ───────────────────────────────────────────────────────

export function CalendarEventForm({ id }: { id?: string }) {
  const router = useRouter();
  const isEdit = !!id;
  const { data: session } = useSession();

  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";

  const [rec, setRec] = useState<Record<string, unknown> | null>(null);
  const [isFetching, setIsFetching] = useState(isEdit);

  const addMutation = useApiPost("event/event-add");
  const editMutation = useApiPost("event/event-update");
  const mutation = isEdit ? editMutation : addMutation;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      title: "",
      priority: "",
      category: "",
      color_code: "",
      start_date: "",
      start_time: "",
      end_date: "",
      end_time: "",
      timezoneValue: "",
      displayed_event_for: "",
      description: "",
      role: [],
      is_recurring: false,
      recurring_days: [],
      subscription_availability: false,
      notification_template: "",
      quarter: [],
      visible_in_frontend: false,
      not_available_slot: false,
      holiday_event: false,
      status: true,
    },
  });

  const isRecurring = form.watch("is_recurring");
  const subscriptionAvail = form.watch("subscription_availability");
  const recurringDays = form.watch("recurring_days") ?? [];
  const roleValue = form.watch("role") ?? [];
  const quarterValue = form.watch("quarter") ?? [];

  // Fetch record for edit mode
  useEffect(() => {
    if (!isEdit || !session?.token) return;
    setIsFetching(true);
    fetch(`${baseUrl}event/single-event-fetch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.token}`,
      },
      body: JSON.stringify({ _id: id }),
    })
      .then((r) => r.json())
      .then((data) => {
        const fetched = data.results?.res ?? data.results ?? data;
        setRec(fetched);
      })
      .catch(() => toast.error("Failed to load record"))
      .finally(() => setIsFetching(false));
  }, [isEdit, session?.token, id, baseUrl]);

  // Populate form when record arrives
  useEffect(() => {
    if (!rec) return;
    form.reset({
      title: (rec.title as string) ?? "",
      priority: rec.priority != null ? String(rec.priority) : "",
      category: (rec.category as string) ?? "",
      color_code: (rec.color_code as string) ?? "",
      start_date: (rec.start_date as string) ?? "",
      start_time: (rec.start_time as string) ?? "",
      end_date: (rec.end_date as string) ?? "",
      end_time: (rec.end_time as string) ?? "",
      timezoneValue: (rec.timezoneValue as string) ?? "",
      displayed_event_for: (rec.displayed_event_for as string) ?? "",
      description: (rec.description as string) ?? "",
      role: Array.isArray(rec.role) ? (rec.role as string[]) : [],
      is_recurring: !!(rec.is_recurring),
      recurring_days: Array.isArray(rec.recurring_days) ? (rec.recurring_days as string[]) : [],
      subscription_availability: !!(rec.subscription_availability),
      notification_template: (rec.notification_template as string) ?? "",
      quarter: Array.isArray(rec.quarter) ? (rec.quarter as string[]) : [],
      visible_in_frontend: !!(rec.visible_in_frontend),
      not_available_slot: !!(rec.not_available_slot),
      holiday_event: !!(rec.holiday_event),
      status: rec.status === 1 || rec.status === true,
    });
  }, [rec, form]);

  // Preset day buttons
  function setDayPreset(preset: "weekend" | "weekdays" | "everyday") {
    const days =
      preset === "weekend" ? WEEKEND : preset === "weekdays" ? WEEKDAYS : EVERYDAY;
    form.setValue("recurring_days", [...days]);
  }

  function toggleDay(day: DayKey) {
    const current = form.getValues("recurring_days") ?? [];
    if (current.includes(day)) {
      form.setValue(
        "recurring_days",
        current.filter((d) => d !== day)
      );
    } else {
      form.setValue("recurring_days", [...current, day]);
    }
  }

  async function onSubmit(values: FormValues) {
    try {
      const timezoneName = TIMEZONE_NAMES[values.timezoneValue] ?? values.timezoneValue;

      const body: Record<string, unknown> = {
        title: values.title,
        priority: parseFloat(values.priority) || 0,
        category: values.category,
        color_code: values.color_code,
        start_date: values.start_date,
        start_time: values.start_time,
        end_date: values.end_date,
        end_time: values.end_time,
        timezoneValue: values.timezoneValue,
        timezoneName,          // C-12
        by_ai: false,          // C-12
        displayed_event_for: values.displayed_event_for,
        description: values.description || undefined,
        role: values.role,
        is_recurring: values.is_recurring,
        recurring_days: values.is_recurring ? values.recurring_days : [],
        subscription_availability: values.subscription_availability,
        notification_template: values.subscription_availability
          ? values.notification_template
          : undefined,
        quarter: values.quarter,
        visible_in_frontend: values.visible_in_frontend,
        not_available_slot: values.not_available_slot,
        holiday_event: values.holiday_event,
        status: values.status ? 1 : 0,
      };

      if (isEdit) body._id = id;

      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Event updated successfully" : "Event created successfully");
      router.push("/admin/calendar-events");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 animate-pulse rounded-md" style={{ background: "hsl(240 20% 16%)" }} />
        <div className="h-96 animate-pulse rounded-xl" style={{ background: "hsl(240 20% 16%)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div
          className="h-6 w-1 rounded-full shrink-0"
          style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }}
        />
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Calendar Event" : "Add Calendar Event"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isEdit ? "Update event details" : "Create a new calendar event"}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* ── Core Details ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Event Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid gap-4 md:grid-cols-2">
                <TextField control={form.control} name="title" label="Event Name" placeholder="Event name" />
                <TextField control={form.control} name="priority" label="Priority" type="number" placeholder="e.g. 1" />
                <TextField control={form.control} name="category" label="Category" placeholder="e.g. Webinar" />
                <TextField control={form.control} name="color_code" label="Color Code" placeholder="#FFFFFF" />
                <DateField control={form.control} name="start_date" label="Start Date" />
                {/* CE-02: Use type="time" for browser-native time picker */}
                <TextField control={form.control} name="start_time" label="Start Time" type="time" />
                <DateField control={form.control} name="end_date" label="End Date" />
                {/* CE-02: Use type="time" for browser-native time picker */}
                <TextField control={form.control} name="end_time" label="End Time" type="time" />
                <SelectField
                  control={form.control}
                  name="timezoneValue"
                  label="Timezone"
                  placeholder="Select timezone"
                  options={TIMEZONE_OPTIONS}
                />
                <SelectField
                  control={form.control}
                  name="displayed_event_for"
                  label="Displayed Event For"
                  placeholder="Select audience"
                  options={[
                    { value: "Public", label: "Public" },
                    { value: "Members-Only", label: "Members-Only" },
                    { value: "Students-Only", label: "Students-Only" },
                    { value: "Members and Guests", label: "Members and Guests" },
                  ]}
                />
                <div className="md:col-span-2">
                  <TextareaField
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder="Event description..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Role (C-06) ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Role Visibility
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <Controller
                control={form.control}
                name="role"
                render={({ fieldState }) => (
                  <CheckboxGroup
                    label="Who can see this event?"
                    options={ROLE_OPTIONS}
                    value={roleValue}
                    onChange={(v) => form.setValue("role", v, { shouldValidate: true })}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </CardContent>
          </Card>

          {/* ── Recurring (C-08) ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Recurring Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <SwitchField
                control={form.control}
                name="is_recurring"
                label="Is Recurring"
                description="Show day-of-week selectors for repeating events"
              />

              {isRecurring && (
                <div className="space-y-3 rounded-lg p-4" style={{ background: "hsl(240 20% 12%)", border: "1px solid hsl(240 20% 22%)" }}>
                  {/* Preset buttons */}
                  <div className="flex flex-wrap gap-2">
                    {(["weekend", "weekdays", "everyday"] as const).map((preset) => {
                      const presetDays =
                        preset === "weekend" ? WEEKEND : preset === "weekdays" ? WEEKDAYS : EVERYDAY;
                      const isActive =
                        presetDays.length === recurringDays.length &&
                        presetDays.every((d) => recurringDays.includes(d));
                      return (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setDayPreset(preset)}
                          className="rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition-colors"
                          style={{
                            background: isActive ? "#ffa800" : "hsl(240 20% 20%)",
                            color: isActive ? "#000" : "inherit",
                            border: `1px solid ${isActive ? "#ffa800" : "hsl(240 20% 28%)"}`,
                          }}
                        >
                          {preset}
                        </button>
                      );
                    })}
                  </div>

                  {/* Individual day checkboxes */}
                  <div className="flex flex-wrap gap-3">
                    {DAYS.map(({ key, label }) => (
                      <label
                        key={key}
                        className="flex cursor-pointer items-center gap-2 text-sm select-none"
                      >
                        <Checkbox
                          checked={recurringDays.includes(key)}
                          onCheckedChange={() => toggleDay(key)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Subscription Availability (C-09) ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Subscription Availability
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <SwitchField
                control={form.control}
                name="subscription_availability"
                label="Subscription Availability"
                description="Enable notification template for this event"
              />
              {/* CE-02: RichHtmlEditor for notification template when subscription is enabled */}
              {subscriptionAvail && (
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Notification Template
                  </label>
                  <Controller
                    control={form.control}
                    name="notification_template"
                    render={({ field }) => (
                      <RichHtmlEditor value={field.value ?? ""} onChange={field.onChange} />
                    )}
                  />
                  {form.formState.errors.notification_template && (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.notification_template.message}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Quarter (C-10) ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Quarter Visibility
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <Controller
                control={form.control}
                name="quarter"
                render={({ fieldState }) => (
                  <CheckboxGroup
                    label="Which quarters should this event appear in?"
                    options={QUARTER_OPTIONS}
                    value={quarterValue}
                    onChange={(v) => form.setValue("quarter", v, { shouldValidate: true })}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </CardContent>
          </Card>

          {/* ── Visibility Switches (C-11) ── */}
          <Card>
            <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                Visibility &amp; Flags
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-3">
              <SwitchField
                control={form.control}
                name="visible_in_frontend"
                label="Visible in Frontend"
                description="Show this event on the public site"
              />
              <SwitchField
                control={form.control}
                name="not_available_slot"
                label="Not Available Slot"
                description="Mark this slot as unavailable"
              />
              <SwitchField
                control={form.control}
                name="holiday_event"
                label="Holiday Event"
                description="Mark as a holiday event"
              />
              <SwitchField
                control={form.control}
                name="status"
                label="Active"
                description="Make this event active"
              />
            </CardContent>
          </Card>

          {/* ── Submit ── */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
            >
              {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <Button type="button" variant="outline" onClick={() => router.back()}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
